import React, { forwardRef, useImperativeHandle, useState, useRef, useCallback } from 'react';
import { 
  Box, 
  Checkbox, 
  ListItemText,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Divider
} from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface DriverPlateFilterProps {
  api: any;
  column: any;
  columnApi: any;
  context: any;
}

const DriverPlateFilter = forwardRef((props: DriverPlateFilterProps, ref) => {
  const { t } = useTranslation();
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [dynamicOptions, setDynamicOptions] = useState<{ value: string, label: string }[]>([]);
  const currentFilterRef = useRef<string[]>([]);

  // Funzione per estrarre la targa attiva dal conducente
  const getActivePlate = (driverData: any): string | null => {
    const today = new Date();
    const activeAssociations = driverData.associations?.filter((a: any) => a.status === "ACTIVE") || [];
    
    const todayIncluded = activeAssociations.find((a: any) => {
      const from = new Date(a.dateFrom);
      // Tratta dateTo null/undefined come periodo aperto (∞)
      if (a.dateTo === null || a.dateTo === undefined) {
        return from <= today;
      }
      const to = new Date(a.dateTo);
      return from <= today && to >= today;
    });

    let selectedAssociation = todayIncluded;

    if (!selectedAssociation) {
      selectedAssociation = activeAssociations
        .filter((a: any) => new Date(a.dateFrom) > today)
        .sort((a: any, b: any) =>
          new Date(a.dateFrom).getTime() - new Date(b.dateFrom).getTime()
        )[0];
    }

    return selectedAssociation?.vehicleLicensePlate || null;
  };

  // Update options when filter is opened or data changes
  const updateOptions = useCallback(() => {
    const rowData: string[] = [];
    props.api.forEachNodeAfterFilterAndSort((node: any) => {
      if (node.data) {
        const plate = getActivePlate(node.data);
        if (plate && plate.trim() !== '') {
          rowData.push(plate);
        }
      }
    });
    const unique = Array.from(new Set(rowData)).filter(value => value && value.trim() !== '');
    setDynamicOptions(unique.map(value => ({ value, label: value })));
  }, [props.api]);

  React.useEffect(() => {
    updateOptions();
    // eslint-disable-next-line
  }, [props.api, props.api.getDisplayedRowCount()]);

  useImperativeHandle(ref, () => ({
    isFilterActive() {
      return currentFilterRef.current.length > 0;
    },
    doesFilterPass(params: any) {
      if (currentFilterRef.current.length === 0) return true;
      const plate = getActivePlate(params.data);
      return plate ? currentFilterRef.current.includes(plate) : false;
    },
    getModel() {
      if (this.isFilterActive()) {
        return {
          type: 'set',
          values: currentFilterRef.current,
        };
      }
      return null;
    },
    setModel(model: any) {
      const newValues = model?.values || [];
      setSelectedValues(newValues);
      currentFilterRef.current = newValues;
    },
    getModelAsString() {
      if (currentFilterRef.current.length === 0) return '';
      return currentFilterRef.current.join(', ');
    }
  }), []);

  const handleCheckboxChange = (value: string) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    setSelectedValues(newValues);
    currentFilterRef.current = newValues;
    props.api.onFilterChanged();
  };

  const handleReset = () => {
    setSelectedValues([]);
    currentFilterRef.current = [];
    props.api.onFilterChanged();
  };

  return (
    <Box sx={{ 
      width: '180px',
      bgcolor: 'background.paper',
      borderRadius: 1,
      boxShadow: 1
    }}>
      <List sx={{ 
        py: 0,
        maxHeight: '200px',
        overflowY: 'auto'
      }}>
        {dynamicOptions.map((option) => (
          <ListItem key={option.value} disablePadding>
            <ListItemButton
              dense
              onClick={() => handleCheckboxChange(option.value)}
              sx={{
                minHeight: '28px',
                py: 0.5,
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: '28px' }}>
                <Checkbox
                  edge="start"
                  checked={selectedValues.includes(option.value)}
                  tabIndex={-1}
                  disableRipple
                  size="small"
                  sx={{ 
                    padding: '2px',
                    '& .MuiSvgIcon-root': {
                      fontSize: '16px'
                    }
                  }}
                />
              </ListItemIcon>
              <ListItemText 
                primary={option.label}
                primaryTypographyProps={{
                  variant: 'body2',
                  color: 'text.primary',
                  sx: {
                    fontSize: '0.75rem',
                    lineHeight: 1.2
                  }
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end',
          p: 0.5
        }}
      >
        <Button
          size="small"
          variant="outlined"
          onClick={handleReset}
          sx={{ 
            minWidth: '60px',
            textTransform: 'none',
            fontSize: '0.75rem',
            py: 0.25,
            px: 1,
            borderColor: 'divider',
            color: '#CF0A2C',
            '&:hover': {
              borderColor: '#CF0A2C',
              backgroundColor: 'action.hover'
            }
          }}
        >
          {t('common.reset')}
        </Button>
      </Box>
    </Box>
  );
});

export default DriverPlateFilter; 